import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Plus, Pencil, Trash2, Lock, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function SchoolAnnouncements() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", image_url: "", published: true });
  const [busy, setBusy] = useState(false);

  const mod = modules.find((m) => m.key === "school_announcements");
  const owned = mod?.owned;

  const load = async () => {
    const [mods, my] = await Promise.all([
      api.get(`${V1}/school-modules`),
      api.get(`${V1}/school/announcements/mine`).catch(() => ({ data: [] })),
    ]);
    setModules(mods.data || []);
    setItems(my.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`${V1}/school/announcements/${editing.id}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post(`${V1}/school/announcements`, form);
        toast.success("Duyuru yayınlandı");
      }
      setOpen(false); setForm({ title: "", body: "", image_url: "", published: true }); setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Duyuruyu silmek istiyor musunuz?")) return;
    await api.delete(`${V1}/school/announcements/${id}`);
    toast.success("Silindi");
    load();
  };

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;

  if (!owned) {
    return (
      <div className="max-w-2xl mx-auto py-8 text-center" data-testid="announce-locked">
        <div className="p-8 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5">
          <Lock className="h-12 w-12 mx-auto text-accent mb-3" />
          <h1 className="font-display text-2xl font-bold">Duyurular Modülü Kilitli</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            <b>{mod.label}</b> — {mod.description}
          </p>
          <div className="grid grid-cols-2 gap-3 mt-6 max-w-xs mx-auto">
            <div className="p-4 rounded-lg border border-border bg-card">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Aylık</div>
              <div className="text-2xl font-display font-bold">₺{mod.price_monthly}</div>
            </div>
            <div className="p-4 rounded-lg border border-accent/40 bg-accent/10">
              <div className="text-xs uppercase tracking-widest text-accent">Yıllık</div>
              <div className="text-2xl font-display font-bold">₺{mod.price_yearly}</div>
              <div className="text-[10px] text-accent">2 ay bedava</div>
            </div>
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            <Button asChild data-testid="upsell-contact">
              <a href="mailto:destek@sanostudent.com?subject=Duyurular Modülü Talebi">
                <ShoppingCart className="h-4 w-4" /> Modülü Talep Et
              </a>
            </Button>
            <Button asChild variant="outline"><Link to="/okul-paneli/modullerim">Tüm Modüller</Link></Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">
            Modül talebiniz süper admin tarafından onaylandıktan sonra hesabınıza tanımlanır.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-announcements-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Modül · {mod.label}</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Megaphone className="h-7 w-7 text-primary" /> Duyurular & Haberler</h1>
          <p className="text-muted-foreground text-sm mt-1">Okulunuzun sayfasında yayınlanacak duyuru ve haberleri yönetin.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ title: "", body: "", image_url: "", published: true }); setOpen(true); }} data-testid="new-announce-btn">
          <Plus className="h-4 w-4" /> Yeni Duyuru
        </Button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="p-12 text-center text-muted-foreground bg-card border border-border rounded-xl">Henüz duyuru yok.</div>
        )}
        {items.map((a) => (
          <div key={a.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`announce-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-lg">{a.title}</div>
                  {!a.published && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Taslak</span>}
                </div>
                <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("tr-TR")}</div>
                <div className="text-sm mt-2 whitespace-pre-line">{a.body}</div>
                {a.image_url && <img src={a.image_url} alt="" className="mt-3 rounded-lg max-h-48 object-cover" />}
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setForm({ title: a.title, body: a.body, image_url: a.image_url || "", published: a.published }); setOpen(true); }} data-testid={`edit-announce-${a.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(a.id)} data-testid={`delete-announce-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="announce-dialog">
          <DialogHeader><DialogTitle>{editing ? "Duyuruyu Düzenle" : "Yeni Duyuru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} data-testid="announce-title" />
            </div>
            <div>
              <Label>Metin *</Label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))} data-testid="announce-body" />
            </div>
            <div>
              <Label>Görsel URL (opsiyonel)</Label>
              <Input value={form.image_url} onChange={(e) => setForm((s) => ({ ...s, image_url: e.target.value }))} placeholder="https://..." data-testid="announce-image" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm((s) => ({ ...s, published: e.target.checked }))} data-testid="announce-published" />
              <span>Yayında (kapatınca taslak olur)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} disabled={busy || !form.title || !form.body} data-testid="announce-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
